import React from 'react';
import {
  FlexRow,
  Text,
  FlexSpacer,
  IconContainer,
  Dropdown,
  Panel,
  DropdownMenuButton,
  IconButton,
} from '@epam/promo';
import { ReactComponent as actionCalendar } from '@epam/assets/icons/common/action-calendar-18.svg';
import { ReactComponent as DeleteIcon } from '@epam/assets/icons/common/action-delete-18.svg';
import { ReactComponent as navigationMoreIcon } from '@epam/assets/icons/common/navigation-more_horiz-18.svg';
import { useDropdown, useFavouriteBroadcastToggle } from 'hooks';
import { useUserRole } from 'hooks/helpers';
import { StatusIndicator } from 'components/common';
import { CardProps } from './Card.model';
import css from './Card.module.scss';
import { FormattedDate } from '../../common/FormattedDate';
import { HighlightedTitle } from '../../common/HighlightedTitle';
import { CardDescription } from '../../common/CardDescription';
import { dateWithTimeToString } from '../../../hooks/helpers/date-time-helper';
import { useFavoritesStore } from 'store/favorites';
import { FavoriteStarToggle } from 'components/common/FavoriteStarToggle/FavoriteStarToggle';
import { AllStatuses } from 'interfaces/statuses';

export const Card = ({ item, isActive, onClick, onDelete }: CardProps) => {
  const { openDropdownMenu } = useDropdown();
  const { isSuperAdmin } = useUserRole();
  const isDateExpired = item.schedule && item.schedule < new Date();
  const favoritesUserId = useFavoritesStore((state) => state.userId);
  const isBroadcastFavorite = useFavoritesStore(
    (state) => !!state.favoriteBroadcastIds[item.id],
  );
  const { mutate: toggleFavouriteBroadcast, isPending: isFavouritePending } =
    useFavouriteBroadcastToggle();

  return (
    <div
      aria-hidden="true"
      className={isActive ? css['card__active-container'] : css.card__inactive}
      role="button"
      tabIndex={0}
      onClick={onClick}>
      <li className={isActive ? `${css.card} ${css.card__active}` : css.card}>
        <FlexRow cx={css.card__schedule} size={null}>
          <IconContainer
            icon={actionCalendar}
            style={{
              fill:
                isDateExpired && item.status !== AllStatuses.PUBLISHED
                  ? 'var(--uui-error-70)'
                  : 'var(--uui-secondary-50)',
            }}
          />
          <Text
            color={
              isDateExpired && item.status !== AllStatuses.PUBLISHED
                ? 'red'
                : item.schedule
                  ? 'gray60'
                  : 'gray50'
            }
            cx={css.card__schedule__text}
            fontSize="12"
            fontWeight="400"
            lineHeight="18"
            rawProps={{
              'data-testid': 'broadcast-schedule',
            }}
            size="18">
            {item.schedule
              ? dateWithTimeToString(item.schedule)
              : 'Unscheduled'}
          </Text>
          <FlexSpacer />
          {favoritesUserId && item.status !== AllStatuses.NOT_SAVED && (
            <div className={css.favourite__star}>
              <FavoriteStarToggle
                ariaLabel={`Toggle favourite for broadcast "${item.title}"`}
                data-testid="broadcast-favourite-star"
                disabled={isFavouritePending}
                isActive={isBroadcastFavorite}
                onToggle={() =>
                  toggleFavouriteBroadcast({
                    broadcastId: item.id,
                    favorite: !isBroadcastFavorite,
                  })
                }
              />
            </div>
          )}
          {isSuperAdmin && item.status !== AllStatuses.PUBLISHED && (
            <Dropdown
              placement="bottom-end"
              renderBody={() => (
                <Panel shadow background="white">
                  <DropdownMenuButton
                    caption="Delete"
                    cx={css['dropdown-button']}
                    icon={DeleteIcon}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.stopPropagation();
                      const target = event.target as HTMLInputElement;

                      onDelete(target.value);
                    }}
                  />
                </Panel>
              )}
              renderTarget={({ ref, onClick: handleMenuItemClick }) => (
                <IconButton
                  ref={ref}
                  icon={navigationMoreIcon}
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    openDropdownMenu(event, handleMenuItemClick)
                  }
                />
              )}
            />
          )}
        </FlexRow>

        <HighlightedTitle title={item.title} />

        <CardDescription description={item.message || 'Message'} />

        <FlexRow cx={css.card__details} size={null}>
          <StatusIndicator size="8" status={item.status} />
          <FlexSpacer />
          <FormattedDate date={item.updatedAt} />
        </FlexRow>
      </li>
    </div>
  );
};
